import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  emitAuthLogin,
  exchangeAdminImpersonation,
  waitForAuthenticatedSession,
} from "../api/api";
import ValphaLogo from "../branding/logo";


const readHandoffToken = () => {
  const fragment = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  return new URLSearchParams(fragment).get("token") || "";
};


const AdminImpersonationLogin = () => {
  const navigate = useNavigate();
  const startedRef = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const rawToken = readHandoffToken();
    // Remove the secret from browser history before making any network call.
    window.history.replaceState(null, "", "/admin-login");

    if (!rawToken) {
      setError("This admin login link is missing its one-time token.");
      return;
    }

    let cancelled = false;
    const exchange = async () => {
      try {
        await exchangeAdminImpersonation(rawToken);
        await waitForAuthenticatedSession();
        if (cancelled) return;
        emitAuthLogin();
        navigate("/dashboard", { replace: true });
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || "This admin login link is invalid or has expired.");
      }
    };
    exchange();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-[#0F1826] font-inter text-white px-4">
      <div className="bg-[#1A2539] p-8 rounded-xl shadow-lg w-full max-w-md text-center">
        <img
          src={ValphaLogo}
          alt="valpha.io"
          className="h-16 w-auto mx-auto mb-5 object-contain"
        />
        {error ? (
          <>
            <h1 className="text-xl font-semibold mb-3">Unable to log in as this user</h1>
            <p className="text-sm text-red-300 mb-6">{error}</p>
            <p className="text-sm text-gray-400 mb-5">
              Return to Django admin and create a fresh link. Links are single-use and expire quickly.
            </p>
            <Link
              to="/login"
              className="inline-flex rounded-md bg-blue-600 px-4 py-2 text-sm font-medium hover:bg-blue-500"
            >
              Go to normal login
            </Link>
          </>
        ) : (
          <>
            <div
              className="h-9 w-9 mx-auto mb-5 rounded-full border-4 border-blue-400 border-t-transparent animate-spin"
              aria-hidden="true"
            />
            <h1 className="text-xl font-semibold mb-2">Opening the user session</h1>
            <p className="text-sm text-gray-400">Verifying the one-time admin link…</p>
          </>
        )}
      </div>
    </div>
  );
};


export default AdminImpersonationLogin;
